import { format } from "date-fns";
import React from "react";
import { TransformedPair } from "./types";
import { TimeView } from "../TimeColumnCell/TimeView";
import { AttributesDisplay } from "../TimeColumnCell/AttributesDisplay";
import { hasShiftSubmissionTimeEnded, TimestampAttributeName } from "../../../shiftUtils";
import { ShiftComponent, ShiftThresholds, TimesheetsShiftInfo } from "../../../types/Shift.types";
import { ArrowIcon, TimestampPairWrapper } from "./styledComponents";
import { MissingTime } from "../styledComponents";

export type AuditTimestampPairDisplayProps = {
  edit: TransformedPair;
  timezone: string;
  shiftThresholds: ShiftThresholds;
  shiftKey: string;
  isHistoricalDeleted?: boolean;
  shiftInfo: TimesheetsShiftInfo;
};

/**
 * Displays a single audit edit entry as a pair of timestamps: start → end
 * Works for both clock in/out AND breaks due to unified TransformedEdit structure
 */
export const AuditTimestampPairDisplay = ({
  edit,
  timezone,
  shiftThresholds,
  shiftKey,
  isHistoricalDeleted = false,
  shiftInfo,
}: AuditTimestampPairDisplayProps) => {
  const convertTZ = (date: Date, timezoneString: string) => {
    return new Date(date.toLocaleString("en-US", { timeZone: timezoneString }));
  };

  const formattedStartTime = edit.start
    ? format(convertTZ(edit.start, timezone), "h:mma").toLowerCase()
    : "";

  const formattedEndTime = edit.end
    ? format(convertTZ(edit.end, timezone), "h:mma").toLowerCase()
    : "";

  return (
    <TimestampPairWrapper deleted={isHistoricalDeleted}>
      {edit.start ? (
        <>
          <TimeView
            isCompleteShiftInDispute={false}
            formattedTime={formattedStartTime}
            missingTimeTooltipMessage="This timestamp has not been submitted"
            isTimestampSubmitted={edit.startAttributes.includes(
              TimestampAttributeName.CM_SUBMITTED,
            )}
            isTimestampMissing={edit.startAttributes.includes(
              TimestampAttributeName.CM_MISSING_SUBMISSION,
            )}
            tooltipAnchor={`audit-start-${shiftKey}`}
            hasShiftSubmissionTimeEnded={hasShiftSubmissionTimeEnded(shiftThresholds)}
          />
          <AttributesDisplay
            attributes={edit.startAttributes}
            timeType={ShiftComponent.CLOCK_IN}
            shiftInfo={shiftInfo}
            tooltipAnchor={`attribute-audit-start-${shiftKey}`}
          />
        </>
      ) : (
        <MissingTimeDisplay
          shiftKey={shiftKey}
          shiftInfo={shiftInfo}
          timeType={ShiftComponent.CLOCK_IN}
        />
      )}
      <ArrowIcon src={`${import.meta.env.VITE_ASSET_BASE_URL}/icons/Timesheets/time-arrow.svg`} />
      {edit.end ? (
        <>
          <TimeView
            isCompleteShiftInDispute={false}
            formattedTime={formattedEndTime}
            missingTimeTooltipMessage="This timestamp has not been submitted"
            isTimestampSubmitted={edit.endAttributes.includes(TimestampAttributeName.CM_SUBMITTED)}
            isTimestampMissing={edit.endAttributes.includes(
              TimestampAttributeName.CM_MISSING_SUBMISSION,
            )}
            tooltipAnchor={`audit-end-${shiftKey}`}
            hasShiftSubmissionTimeEnded={hasShiftSubmissionTimeEnded(shiftThresholds)}
          />
          <AttributesDisplay
            attributes={edit.endAttributes}
            timeType={ShiftComponent.CLOCK_OUT}
            shiftInfo={shiftInfo}
            tooltipAnchor={`attribute-audit-end-${shiftKey}`}
          />
        </>
      ) : (
        <MissingTimeDisplay
          shiftKey={shiftKey}
          shiftInfo={shiftInfo}
          timeType={ShiftComponent.CLOCK_OUT}
        />
      )}
    </TimestampPairWrapper>
  );
};

const MissingTimeDisplay = (props: { shiftKey: string, shiftInfo: TimesheetsShiftInfo, timeType: ShiftComponent }) => (
  <>
    <MissingTime>Missing</MissingTime>
    <AttributesDisplay
      attributes={[TimestampAttributeName.CM_MISSING_SUBMISSION]}
      timeType={props.timeType}
      shiftInfo={props.shiftInfo}
      tooltipAnchor={`break-attribute-${props.shiftKey}`}
    />
  </>
);
